import { useQuery } from '@tanstack/react-query';
import { getBalance, GetBalanceReturnType, ReadContractParameters, readContracts } from '@wagmi/core';
import invariant from 'invariant';
import { Address, ContractFunctionParameters, erc20Abi, zeroAddress } from 'viem';

import { config } from '@/app/providers/app/wagmiConfig';
import { ETH_ADDRESS } from '@/app/shared/constants/addresses';

export const useBalances = ({
  enabled = true,
  address,
  tokens,
}: {
  tokens: Address[];
  address?: Address;
  enabled?: boolean;
}) => {
  const queryFn = async (): Promise<Record<string, bigint> | null> => {
    invariant(address, 'Wallet is not connected or missing address');
    let nativeToken: Address | undefined;
    const noNativeTokens: Address[] = [];
    const tokensBalance: Record<string, bigint> = {};
    const contracts: ReadContractParameters[] = [];

    tokens.forEach((token) => {
      if (token === zeroAddress || token.toLowerCase() === ETH_ADDRESS) {
        nativeToken = token;
        return;
      }

      noNativeTokens.push(token);
      contracts.push({
        address: token,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address],
      });
    });

    const resultBalances = await readContracts(config, {
      contracts: contracts as ContractFunctionParameters[],
    });

    if (resultBalances) {
      resultBalances.forEach((balance, index) => {
        if (balance?.status === 'success' && typeof balance?.result === 'bigint') {
          const token: Address = noNativeTokens[index];
          if (token) {
            tokensBalance[token.toLowerCase()] = balance.result;
          }
        }
      });
    }

    if (nativeToken) {
      const resultNativeBalance: GetBalanceReturnType = await getBalance(config, { address });
      tokensBalance[nativeToken.toLowerCase()] = resultNativeBalance.value;
    }
    return tokensBalance;
  };

  const { data, isLoading, isPending, error, refetch } = useQuery({
    queryKey: ['useBalances', address, tokens.map((token) => token.toLowerCase()).join(',')],
    enabled: !!(address && tokens.length && enabled),
    queryFn: () => queryFn(),
  });

  return {
    data,
    isLoading: isLoading || isPending,
    error,
    refetch
  };
};